import { ActionBar } from "@/components/ActionBar/ActionBar";
import { DeleteTemplateButton } from "@/components/Button/Templates/DeleteButton";
import { EditTemplateButton } from "@/components/Button/Templates/EditButton";
import { NoTemplates } from "@/components/EmptyState/TemplateEmptyState";
import { DisplayError } from "@/components/Error/Error";
import { ReduxPerPage } from "@/components/PerPageSelector/PerPageSelector";
import { SearchField } from "@/components/SearchField/SearchField";
import { Column, DynamicTable, Sort } from "@/components/Table/Table";
import { PH_NO_CAPTURE } from "@/constants/posthog";
import { TEMPLATE_TABLE_STATE_NAME } from "@/constants/templates";
import {
  TemplateReadPublic,
  useGetTemplatesApiV1TemplatesGetQuery,
  UserReadDto,
  useSearchUsersApiV1UsersSearchGetQuery
} from "@/store/baseClientApi";
import { clientApi } from "@/store/clientApi";
import {
  invalidate,
  selectIsChecked,
  selectNumChecked,
  selectQueryArgs,
  selectQueryIsSearching,
  selectQuerySearchValue,
  selectRtkData,
  setPerPage,
  setRtkArgs,
  setSearchValue,
  setSortField,
  setSortOrder
} from "@/store/slices/tableSlice";
import { RootState, store } from "@/store/store";
import { formatDateTime } from "@/utils/date";
import { Box, LinearProgress, Typography } from "@mui/material";
import { createSelector } from "@reduxjs/toolkit";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { shallowEqual, useSelector } from "react-redux";

const selectData = (state: RootState) =>
  selectRtkData(state, TEMPLATE_TABLE_STATE_NAME, clientApi.endpoints.getTemplatesApiV1TemplatesGet.select);

const selectCreatedByData = createSelector(
  [
    (state: RootState) => {
      const templates = selectData(state)?.data?.content;
      if (templates === undefined || templates.length === 0) {
        return undefined;
      }

      return clientApi.endpoints.searchUsersApiV1UsersSearchGet.select(constructCreatedByQuery(templates))(state)?.data?.content;
    },
    (state: RootState) => selectData(state)?.data?.content
  ],
  (users, templates) =>
    templates?.reduce(
      (acc, template) => {
        return { ...acc, [template.id]: users?.find((user) => user.user_id === template.created_by) };
      },
      {} as { [id: string]: UserReadDto | undefined }
    ),
  {
    memoizeOptions: {
      resultEqualityCheck: shallowEqual
    }
  }
);

const constructCreatedByQuery = (templates?: TemplateReadPublic[]) => ({
  query: templates?.map((template) => `user_id:"${template.created_by}"`).join(" OR "),
  perPage: 50 // There can be up to 50 templates shown at once therefore worse case we need to fetch 50 users
});

enum TemplateColumns {
  Name = "name",
  DesignPart = "designPart",
  CreatedOn = "createdAt",
  CreatedBy = "createdBy"
}

const TemplatesData = () => {
  const queryArgs = useSelector((state: RootState) => selectQueryArgs(state, TEMPLATE_TABLE_STATE_NAME));
  const rtkArgs = useMemo(() => {
    return {
      page: queryArgs.page !== 0 ? queryArgs.page - 1 : queryArgs.page,
      perPage: queryArgs.perPage,
      query: queryArgs.searchValue !== "" ? `name:"*${queryArgs.searchValue}*"` : undefined,
      sorting: queryArgs.sortField !== "" ? queryArgs.sortField + ":" + (queryArgs.sortOrder === "asc" ? "1" : "-1") : undefined
    };
  }, [queryArgs]);

  useEffect(() => {
    store.dispatch(setRtkArgs({ name: TEMPLATE_TABLE_STATE_NAME, value: rtkArgs }));
  }, [rtkArgs]);

  const { data } = useGetTemplatesApiV1TemplatesGetQuery(rtkArgs, {});
  useSearchUsersApiV1UsersSearchGetQuery(constructCreatedByQuery(data?.content), {
    skip: data?.content === undefined || data?.content.length === 0
  });

  return <></>;
};

const SearchBar = () => {
  const { t } = useTranslation("template");
  const isSelected = useSelector((state: RootState) => selectIsChecked(state, TEMPLATE_TABLE_STATE_NAME));
  const searchValue = useSelector((state: RootState) => selectQuerySearchValue(state, TEMPLATE_TABLE_STATE_NAME)) ?? "";

  return (
    <Box
      component="div"
      sx={{
        display: "flex",
        justifyContent: "space-between",
        gap: "0.5em"
      }}
    >
      {isSelected ? (
        <ActionBar
          selector={(state: RootState) => selectNumChecked(state, TEMPLATE_TABLE_STATE_NAME)}
          handleCloseActionBar={() => {
            store.dispatch(invalidate(TEMPLATE_TABLE_STATE_NAME));
          }}
        >
          <EditTemplateButton />
          <DeleteTemplateButton />
        </ActionBar>
      ) : (
        <>
          <Box
            component="div"
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              gap: "0.5em"
            }}
          >
            <SearchField
              placeholder={t("label.search")}
              value={searchValue}
              onChange={(searchInput) => {
                store.dispatch(
                  setSearchValue({
                    name: TEMPLATE_TABLE_STATE_NAME,
                    value: searchInput
                  })
                );
                store.dispatch(invalidate(TEMPLATE_TABLE_STATE_NAME));
              }}
            />
          </Box>
          <ReduxPerPage
            selector={(state: RootState) => state.tables.data[TEMPLATE_TABLE_STATE_NAME]?.queryArgs?.perPage}
            dispatch={(state) => store.dispatch(setPerPage({ name: TEMPLATE_TABLE_STATE_NAME, value: state }))}
          />
        </>
      )}
    </Box>
  );
};

const TemplateRowCell = ({ column, id }: { column: Column; id: string }) => {
  const { t } = useTranslation("template");
  const UNAVAILABLE = t("common:loading.unavailable");
  const template = useSelector((state: RootState) => selectData(state)?.data?.content?.find((template) => template.id.toString() === id));
  const userName = useSelector((state: RootState) => selectCreatedByData(state)?.[id]?.name);

  switch (column.key as TemplateColumns) {
    case TemplateColumns.Name:
      return (
        <Box className={PH_NO_CAPTURE} sx={{ containerType: "inline-size", width: "100%" }}>
          <Typography noWrap sx={{ cursor: "text", userSelect: "text" }} onMouseDown={(e) => e.stopPropagation()}>
            {template?.name ?? UNAVAILABLE}
          </Typography>
        </Box>
      );
    case TemplateColumns.DesignPart:
      return (
        <Typography noWrap sx={{ cursor: "text", userSelect: "text" }} onMouseDown={(e) => e.stopPropagation()}>
          {t("label.studyModel")}
        </Typography>
      );
    case TemplateColumns.CreatedOn:
      return (
        <Typography noWrap sx={{ cursor: "text", userSelect: "text" }} onMouseDown={(e) => e.stopPropagation()}>
          {template?.created_at ? formatDateTime(template.created_at) : UNAVAILABLE}
        </Typography>
      );
    case TemplateColumns.CreatedBy:
      return (
        <Box className={PH_NO_CAPTURE} sx={{ containerType: "inline-size", width: "100%" }}>
          <Typography noWrap sx={{ cursor: "text", userSelect: "text" }} onMouseDown={(e) => e.stopPropagation()}>
            {userName ?? UNAVAILABLE}
          </Typography>
        </Box>
      );
    default:
      return <></>;
  }
};

const TemplatesTable = () => {
  const { t } = useTranslation("template");
  const isSearching = useSelector((state: RootState) => selectQueryIsSearching(state, TEMPLATE_TABLE_STATE_NAME));
  const selectTemplateIds = createSelector(
    [
      (state: RootState) => selectData(state)?.data?.content,
      (state: RootState) => selectData(state)?.isLoading,
      (state: RootState) => selectData(state)?.data?.total_count,
      (state: RootState) => selectData(state)?.error,
      (state: RootState) => selectData(state)?.isUninitialized
    ],
    (templates, isLoading, totalCount, error, isUninitialized) => ({
      templateIds: templates?.map((template) => template.id.toString()),
      error: error,
      totalCount: totalCount ?? templates?.length,
      isLoading: isLoading && (isUninitialized || templates === undefined)
    }),
    {
      memoizeOptions: {
        resultEqualityCheck: shallowEqual
      }
    }
  );

  const { templateIds, error, totalCount, isLoading } = useSelector(selectTemplateIds);

  const columns: Column[] = [
    { name: t("label.name"), sortable: true, key: TemplateColumns.Name, minWidth: 200 },
    { name: t("label.designPart"), sortable: false, key: TemplateColumns.DesignPart, minWidth: 175 },
    { name: t("label.createdOn"), sortable: false, key: TemplateColumns.CreatedOn, minWidth: 175 },
    { name: t("label.createdBy"), sortable: false, key: TemplateColumns.CreatedBy, minWidth: 200 }
  ];

  const handleSortTable = (sort?: Sort) => {
    if (sort !== undefined && sort.key !== undefined) {
      store.dispatch(setSortField({ name: TEMPLATE_TABLE_STATE_NAME, value: sort.key }));
      store.dispatch(setSortOrder({ name: TEMPLATE_TABLE_STATE_NAME, value: sort.order }));
    } else {
      store.dispatch(setSortField({ name: TEMPLATE_TABLE_STATE_NAME, value: TemplateColumns.Name }));
      store.dispatch(setSortOrder({ name: TEMPLATE_TABLE_STATE_NAME, value: "asc" }));
    }
  };

  if (error && templateIds === undefined) {
    // Only show error if there is no data to show
    return <DisplayError error={error} />;
  }

  if (isLoading || templateIds === undefined) {
    return (
      <Box
        component="div"
        sx={{
          height: "100%"
        }}
      >
        <LinearProgress />
      </Box>
    );
  }

  return (
    <>
      <DynamicTable
        stateName={TEMPLATE_TABLE_STATE_NAME}
        checkable={true}
        columns={columns}
        tableData={templateIds ?? []}
        totalNumEntities={totalCount ?? 0}
        onSort={handleSortTable}
        noMatch={isSearching && totalCount === 0}
        renderEntry={(id) => columns.map((column) => <TemplateRowCell key={id} column={column} id={id} />)}
        menuOptions={[]}
        selectable
        mapId={(id) => id}
      />
      {!isSearching && totalCount === 0 && <NoTemplates />}
    </>
  );
};

export const TemplatesList = () => {
  return (
    <>
      <TemplatesData />
      <Box
        component="div"
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          width: "100%",
          pt: "0.2em"
        }}
      >
        <SearchBar />
        <TemplatesTable />
      </Box>
    </>
  );
};
